/**
 * /playground — thu giai captcha ngay tren dashboard, va lay doan ma tuong ung.
 *
 * ============================================================================
 *  TRANG NAY LAM HAI VIEC CUNG LUC, VA DO LA CHU DICH.
 *
 *  Truoc no, dashboard co 11 trang van hanh nhung khong co cho nao de THU. Nguoi vua
 *  cai xong doc README, thay "da chay", roi ket o cau hoi "gio sao?". Toan bo huong dan
 *  API la mot doan curl viet tay o man hinh tao key — chi mot trong nam loai captcha,
 *  dung `<base64>` lam cho trong, va khong he nhac API native.
 *
 *  O day: chon loai -> nhap du lieu -> bam giai -> nhan CA ket qua that LAN doan ma da
 *  tao ra ket qua do. Tai lieu khong the lech voi thuc te, vi no chinh la request vua
 *  chay. Va khi that bai, trang noi ro THIEU BUOC NAO chu khong chi mot dau X — vi tren
 *  mot ban cai moi thi hai trong nam loai chac chan chua giai duoc, va do la binh thuong.
 * ============================================================================
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ProfileDto } from '@capforge/shared';
import { Badge } from '../components/Badge';
import { CopyButton } from '../components/CopyButton';
import { Spinner } from '../components/Spinner';
import { adminFetch, api, errorMessage, getAuth } from '../lib/api';
import { CODE_LANGS, generateCode, type CodeLang } from './playground/codegen';
import { explainReason } from './playground/reasons';
import {
  TYPE_INFO,
  makeSampleAltchaChallenge,
  typeInfo,
  type NativeType,
} from './playground/types';

/** Khoa luu key thu nghiem. sessionStorage chu khong phai localStorage: day la bi mat. */
const PLAYGROUND_KEY = 'capforge.playgroundKey';

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

/**
 * Solver TU CHUNG MINH duoc minh dung — chi nhung cai nay moi duoc len thang `live`.
 *
 * `pow` giai mot bai toan bam: ket qua hoac thoa challenge hoac khong, va `confidence`
 * luon bang 1. Khong co khai niem "doan sai" nen khong can thuc te xac nhan.
 *
 * Moi solver con lai (`hashgrid`, `ocr`, `rotate`, `text`) DOAN. Dap an cua chung phai
 * duoc site dich xac nhan qua `report()` truoc khi duoc phuc vu 100% — do la ly do
 * `canary` ton tai, va la bac bat buoc tren duong len `live`.
 */
const SELF_VERIFYING_SOLVERS: ReadonlySet<string> = new Set(['pow']);

/**
 * Nut "bat profile" o trang nay dua profile len mode nao.
 *
 * ==========================================================================
 *  VI SAO KHONG PHAI LUC NAO CUNG `live`:
 *
 *  Ban dau nut nay luon dat `mode: 'live'` — mot cu bam, bo qua ca thang
 *  off -> shadow -> canary -> live va toan bo cong thang cap (>=200 bao cao,
 *  do chinh xac >=95%).
 *
 *  Voi `pow` thi vo hai. Voi `hashgrid` thi khong: kieu hong cua no KHONG tang dan.
 *  Tu dien bam theo hinh hoc anh, site doi CSS mot dong la lech 24 bit va TOAN BO dap
 *  an sai cung luc. O `canary 5%` ta thay dieu do qua vai bao cao sai; o `live` ta thay
 *  no qua 100% luoi hong tren moi domain dang chay.
 * ==========================================================================
 */
function targetModeFor(profile: ProfileDto): 'live' | 'canary' {
  return SELF_VERIFYING_SOLVERS.has(profile.solver) ? 'live' : 'canary';
}

interface SolveResult {
  ok: boolean;
  answer?: string;
  solution?: Record<string, unknown>;
  confidence?: number;
  source?: string;
  solver?: string;
  profileKey?: string | null;
  latencyMs?: number;
  jobId?: string;
  reason?: string;
  message?: string;
}

function readStoredKey(): string | null {
  try {
    return window.sessionStorage.getItem(PLAYGROUND_KEY);
  } catch {
    return null;
  }
}

function storeKey(value: string): void {
  try {
    window.sessionStorage.setItem(PLAYGROUND_KEY, value);
  } catch {
    /* che do rieng tu — phien nay van chay duoc bang bien trong bo nho */
  }
}

export default function Playground(): JSX.Element {
  const [type, setType] = useState<NativeType>('text');

  // --- dau vao theo tung loai ---
  const [text, setText] = useState('mười cộng năm bằng mấy?');
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<string>('');

  // --- tuy chon ---
  const [profileKey, setProfileKey] = useState('');
  const [timeoutMs, setTimeoutMs] = useState(30_000);
  const [rows, setRows] = useState(3);
  const [columns, setColumns] = useState(3);
  const [comment, setComment] = useState('');
  const [angle, setAngle] = useState(40);
  const [showOptions, setShowOptions] = useState(false);

  // --- trang thai ---
  const [solveKey, setSolveKey] = useState<string | null>(() => readStoredKey());
  const [creatingKey, setCreatingKey] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SolveResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastBody, setLastBody] = useState<Record<string, unknown> | null>(null);
  const [lang, setLang] = useState<CodeLang>('curl');
  const [revealKey, setRevealKey] = useState(false);
  const [profiles, setProfiles] = useState<ProfileDto[]>([]);
  const [enabling, setEnabling] = useState(false);
  const [dragging, setDragging] = useState(false);

  const fileRef = useRef<HTMLInputElement | null>(null);
  const info = typeInfo(type);

  // Key cua chinh phien dashboard dung duoc luon NEU no co scope solve. Khong doan —
  // thu that, va neu bi tu choi thi bao nguoi dung tao key rieng.
  const auth = getAuth();
  const effectiveKey = solveKey ?? (auth?.mode === 'bearer' ? auth.key : null);

  useEffect(() => {
    api
      .profiles()
      .then(setProfiles)
      .catch(() => setProfiles([]));
  }, []);

  useEffect(() => {
    if (type === 'pow' && challenge.length === 0) {
      void makeSampleAltchaChallenge().then((c) => setChallenge(JSON.stringify(c, null, 2)));
    }
  }, [type, challenge.length]);

  // ==========================================================================
  //  Profile khop loai dang chon co cai nao dang PHUC VU khong.
  //
  //  Moi profile duoc seed deu bat dau o mode `off` — co y, vi khong bao gio duoc tu
  //  dong phuc vu dap an chua ai kiem chung. Nhung hau qua o trang nay la: nguoi vua
  //  cai xong bam "Giai captcha" va nhan `no_solver` cho ca loai captcha DE NHAT.
  //
  //  Thay vi de ho di tim, hien thang trang thai o day kem mot nut bat. Van la mot
  //  hanh dong CO Y THUC cua nguoi dung, chi la khong bat ho phai lac duong.
  // ==========================================================================
  //
  //  HAI CHO TUNG SAI o day, ca hai deu day nguoi dung di sai huong:
  //
  //  1. `canary` BI COI LA KHONG PHUC VU. No co phuc vu — `canaryPercent`% request dung
  //     dap an noi bo (xem dispatcher). Coi no ngang `off` lam trang nay bao mot cau SAI
  //     ("nen se khong phuc vu") roi moi nguoi dung bam mot nut dua thang len `live` —
  //     vuot qua toan bo cong thang cap, moi lan mo trang lai moi mot lan.
  //
  //  2. Profile DA TAT van duoc dem. `image-grid` la mau de sao chep (`enabled: false`),
  //     nen no khong bao gio phuc vu; de no lot vao day thi nut "bat" se sua nham chinh
  //     no va nguoi dung tuong minh vua bat mot thu gi do len.
  //
  const matching = useMemo(
    () => profiles.filter((p) => p.taskType === info.taskType && p.enabled),
    [profiles, info.taskType],
  );
  /** Dang phuc vu that: `live` (100%) hoac `canary` (mot phan). */
  const servingProfile = matching.find((p) => p.mode === 'live' || p.mode === 'canary') ?? null;
  /** Chua phuc vu: `off` (tat han) hoac `shadow` (chi do, van tra dap an upstream). */
  const idleProfile = matching.find((p) => p.mode === 'off' || p.mode === 'shadow') ?? null;

  async function enableProfile(profile: ProfileDto): Promise<void> {
    setEnabling(true);
    setError(null);
    try {
      const mode = targetModeFor(profile);
      const body: Record<string, unknown> = { mode };
      // `canary` voi ty le 0 thi khong phuc vu gi ca — nut se khong lam duoc gi ma cung
      // khong bao gi. Dat mot ty le co thuc luon.
      if (mode === 'canary' && profile.canaryPercent <= 0) body['canaryPercent'] = 10;

      await adminFetch<ProfileDto>(`/admin/profiles/${encodeURIComponent(profile.key)}`, {
        method: 'PATCH',
        body,
      });
      setProfiles(await api.profiles());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setEnabling(false);
    }
  }

  // --- nap anh -------------------------------------------------------------

  const loadFile = useCallback((file: File): void => {
    if (file.size > MAX_IMAGE_BYTES) {
      setError(`Ảnh ${(file.size / 1024 / 1024).toFixed(1)}MB, vượt trần 8MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageDataUrl(typeof reader.result === 'string' ? reader.result : null);
      setImageName(file.name);
      setError(null);
    };
    reader.readAsDataURL(file);
  }, []);

  // Dan anh tu clipboard — cach nhanh nhat khi vua chup man hinh mot captcha.
  useEffect(() => {
    if (info.input !== 'image') return;
    const onPaste = (e: ClipboardEvent): void => {
      const item = Array.from(e.clipboardData?.items ?? []).find((i) => i.type.startsWith('image/'));
      const file = item?.getAsFile();
      if (file) loadFile(file);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [info.input, loadFile]);

  // --- tao key thu nghiem --------------------------------------------------

  async function createSolveKey(): Promise<void> {
    setCreatingKey(true);
    setError(null);
    try {
      const res = await adminFetch<{ plaintextKey: string }>('/admin/keys', {
        method: 'POST',
        body: { label: 'playground', scopes: ['solve'], rateLimitPerMin: 120 },
      });
      setSolveKey(res.plaintextKey);
      storeKey(res.plaintextKey);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setCreatingKey(false);
    }
  }

  // --- dung body -----------------------------------------------------------

  function buildBody(): { body: Record<string, unknown> } | { err: string } {
    const body: Record<string, unknown> = { type, timeoutMs };
    if (profileKey.length > 0) body['profileKey'] = profileKey;

    if (info.input === 'text') {
      if (text.trim().length === 0) return { err: 'Nhập câu hỏi trước đã.' };
      body['text'] = text.trim();
    } else if (info.input === 'image') {
      if (imageDataUrl === null) return { err: 'Chọn một ảnh trước đã.' };
      body['image'] = imageDataUrl;
      const c: Record<string, unknown> = {};
      if (type === 'grid') {
        c['rows'] = rows;
        c['columns'] = columns;
        if (comment.trim().length > 0) c['comment'] = comment.trim();
      }
      if (type === 'rotate') c['angle'] = angle;
      if (Object.keys(c).length > 0) body['constraints'] = c;
    } else {
      try {
        body['challenge'] = JSON.parse(challenge) as unknown;
      } catch {
        return { err: 'Challenge không phải JSON hợp lệ.' };
      }
    }
    return { body };
  }

  async function run(): Promise<void> {
    const built = buildBody();
    if ('err' in built) {
      setError(built.err);
      return;
    }
    if (effectiveKey === null) {
      setError('Chưa có key có scope `solve`. Bấm "Tạo key thử nghiệm" ở trên.');
      return;
    }

    setRunning(true);
    setError(null);
    setResult(null);
    setLastBody(built.body);

    try {
      const res = await fetch('/v1/solve', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${effectiveKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(built.body),
      });
      const data = (await res.json()) as SolveResult;
      setResult(data);
      // 401/403 tu API native = key thieu scope `solve`. Thuong xay ra khi nguoi dung
      // dang nhap bang admin token (khong phai Bearer) hoac key chi co scope admin.
      if (res.status === 401 || res.status === 403) {
        setError('Key hiện tại không dùng được cho API native. Tạo một key thử nghiệm ở trên.');
      }
    } catch (err) {
      setError(`Không gọi được /v1/solve: ${errorMessage(err)}`);
    } finally {
      setRunning(false);
    }
  }

  const code = useMemo(() => {
    if (lastBody === null) return null;
    return generateCode(lang, {
      baseUrl: window.location.origin,
      apiKey: effectiveKey,
      body: lastBody,
      revealKey,
    });
  }, [lang, lastBody, effectiveKey, revealKey]);

  const help = result !== null && result.ok === false ? explainReason(result.reason ?? '', type) : null;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold">Thử giải captcha</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
          Chọn loại, nhập dữ liệu, bấm giải. Bạn nhận được cả kết quả thật lẫn đoạn mã đã tạo ra
          nó — copy là chạy được ở project của bạn. Khi không giải được, trang sẽ nói rõ thiếu bước
          chuẩn bị nào.
        </p>
      </header>

      {/* --- key --- */}
      <div className="cf-card flex flex-wrap items-center gap-3 p-3">
        <span className="text-sm text-slate-600 dark:text-slate-400">Key gọi API native:</span>
        {effectiveKey === null ? (
          <>
            <Badge tone="warn">chưa có</Badge>
            <span className="text-xs text-slate-500">
              API native đòi key có scope <code className="cf-mono">solve</code>. Bạn đang đăng nhập
              bằng admin token nên phải tạo một key riêng.
            </span>
          </>
        ) : (
          <>
            <Badge tone="success">{solveKey !== null ? 'key thử nghiệm' : 'key đang đăng nhập'}</Badge>
            <code className="cf-mono text-xs text-slate-500">
              {effectiveKey.slice(0, 12)}…{effectiveKey.slice(-4)}
            </code>
          </>
        )}
        <button
          type="button"
          onClick={() => void createSolveKey()}
          disabled={creatingKey}
          className="cf-btn cf-btn-ghost ml-auto text-xs"
        >
          {creatingKey ? 'Đang tạo…' : 'Tạo key thử nghiệm'}
        </button>
      </div>

      {/* --- chon loai --- */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {TYPE_INFO.map((t) => {
          const active = t.id === type;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setType(t.id);
                setResult(null);
                setError(null);
              }}
              className={`rounded-lg border p-3 text-left transition ${
                active
                  ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500 dark:bg-sky-950'
                  : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">{t.label}</span>
                {t.worksOutOfBox ? (
                  <Badge tone="success">không cần model</Badge>
                ) : (
                  <Badge tone="neutral">cần model / dữ liệu</Badge>
                )}
              </div>
              <p className="mt-1 font-mono text-[10px] text-slate-400">{t.taskType}</p>
              <p className="mt-1 text-xs leading-4 text-slate-500 dark:text-slate-400">{t.what}</p>
            </button>
          );
        })}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* ================= cot trai: dau vao ================= */}
        <div className="space-y-4">
          <div className="cf-card p-4">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="font-semibold">Đầu vào</h2>
              <span className="text-xs text-slate-500">
                solver <code className="cf-mono">{info.solver}</code>
              </span>
            </div>

            {!info.worksOutOfBox && (
              <p className="mb-3 rounded-md bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                {info.needs}
              </p>
            )}

            {/* Trang thai profile — noi truoc con hon de nguoi dung dam vao `no_solver`. */}
            {matching.length === 0 ? (
              <p className="mb-3 rounded-md bg-slate-100 p-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                Chưa có profile nào cho <code className="cf-mono">{info.taskType}</code>. Tạo ở trang
                Profile, hoặc để trống <code className="cf-mono">profileKey</code> và dựa vào nhận diện
                tự động.
              </p>
            ) : servingProfile === null ? (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                <span>
                  Profile <code className="cf-mono">{idleProfile?.key}</code> đang ở mode{' '}
                  <strong>{idleProfile?.mode}</strong> nên sẽ không phục vụ. Mọi profile đều bắt đầu
                  ở <code className="cf-mono">off</code> — có chủ ý.
                  {idleProfile && targetModeFor(idleProfile) === 'canary' && (
                    <>
                      {' '}
                      Solver <code className="cf-mono">{idleProfile.solver}</code> đoán đáp án nên
                      phải qua <code className="cf-mono">canary</code> trước; lên{' '}
                      <code className="cf-mono">live</code> là quyết định ở trang Profile, sau khi
                      đọc cổng thăng cấp.
                    </>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => idleProfile && void enableProfile(idleProfile)}
                  disabled={enabling}
                  className="cf-btn cf-btn-ghost ml-auto text-xs"
                >
                  {enabling
                    ? 'Đang bật…'
                    : idleProfile
                      ? `Bật sang ${targetModeFor(idleProfile)}`
                      : 'Bật'}
                </button>
              </div>
            ) : servingProfile.mode === 'canary' ? (
              // `canary` la trang thai BINH THUONG cua mot profile dang duoc danh gia —
              // khong phai thieu sot can sua. Khong co nut "bat len live" o day: len live
              // la mot quyet dinh doc cong thang cap o trang Profile, khong phai mot cu
              // bam tien tay tu trang thu nghiem.
              <p className="mb-3 text-xs text-amber-700 dark:text-amber-400">
                Profile <code className="cf-mono">{servingProfile.key}</code> đang ở{' '}
                <strong>canary</strong> — chỉ {servingProfile.canaryPercent}% request dùng đáp án
                nội bộ, phần còn lại đi fallback. Thử ở đây có thể lúc được lúc không, đó là đúng.
              </p>
            ) : (
              <p className="mb-3 text-xs text-emerald-700 dark:text-emerald-400">
                Profile <code className="cf-mono">{servingProfile.key}</code> đang phục vụ.
              </p>
            )}

            {info.input === 'text' && (
              <div className="space-y-2">
                <textarea
                  className="cf-input min-h-[5rem] w-full font-mono text-sm"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="vd: mười cộng năm bằng mấy?"
                />
                <div className="flex flex-wrap gap-1.5">
                  {['mười cộng năm bằng mấy?', 'bay tru hai', 'XII + III', 'ký tự thứ 3 của CAPFORGE'].map(
                    (s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setText(s)}
                        className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-800"
                      >
                        {s}
                      </button>
                    ),
                  )}
                </div>
              </div>
            )}

            {info.input === 'image' && (
              <div className="space-y-2">
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    const f = e.dataTransfer.files[0];
                    if (f) loadFile(f);
                  }}
                  onClick={() => fileRef.current?.click()}
                  className={`flex min-h-[9rem] cursor-pointer items-center justify-center rounded-lg border-2 border-dashed p-3 transition ${
                    dragging
                      ? 'border-sky-500 bg-sky-50 dark:bg-sky-950'
                      : 'border-slate-300 hover:border-slate-400 dark:border-slate-600'
                  }`}
                >
                  {imageDataUrl !== null ? (
                    <img
                      src={imageDataUrl}
                      alt="captcha"
                      className="max-h-40 max-w-full rounded"
                    />
                  ) : (
                    <div className="text-center text-sm text-slate-500 dark:text-slate-400">
                      <p>Kéo thả ảnh vào đây</p>
                      <p className="mt-1 text-xs">hoặc bấm để chọn tệp, hoặc dán bằng Ctrl+V</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) loadFile(f);
                  }}
                />
                {imageName !== null && (
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="cf-mono truncate">{imageName}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setImageDataUrl(null);
                        setImageName(null);
                      }}
                      className="cf-link"
                    >
                      xoa
                    </button>
                  </div>
                )}
              </div>
            )}

            {info.input === 'pow' && (
              <div className="space-y-2">
                <textarea
                  className="cf-input min-h-[11rem] w-full font-mono text-xs"
                  value={challenge}
                  onChange={(e) => setChallenge(e.target.value)}
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      void makeSampleAltchaChallenge().then((c) =>
                        setChallenge(JSON.stringify(c, null, 2)),
                      );
                    }}
                    className="cf-btn cf-btn-ghost text-xs"
                  >
                    Sinh challenge mẫu khác
                  </button>
                  <span className="text-xs text-slate-500">
                    Challenge thật lấy từ <code className="cf-mono">/altcha/challenge</code> của site.
                    capforge <strong>không</strong> tự gọi hộ — làm vậy là mở đường SSRF vào mạng nội bộ.
                  </span>
                </div>
              </div>
            )}

            {/* --- tuy chon --- */}
            <button
              type="button"
              onClick={() => setShowOptions((v) => !v)}
              className="mt-3 text-xs text-slate-500 hover:underline dark:text-slate-400"
            >
              {showOptions ? '▾' : '▸'} Tuỳ chọn
            </button>

            {showOptions && (
              <div className="mt-2 grid gap-3 rounded-md bg-slate-50 p-3 sm:grid-cols-2 dark:bg-slate-800/50">
                <div>
                  <label className="cf-label" htmlFor="pg-profile">
                    profileKey
                  </label>
                  <select
                    id="pg-profile"
                    className="cf-input"
                    value={profileKey}
                    onChange={(e) => setProfileKey(e.target.value)}
                  >
                    <option value="">— tự nhận diện —</option>
                    {profiles
                      .filter((p) => p.key !== '__unknown__')
                      .map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.key} ({p.mode})
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="cf-label" htmlFor="pg-timeout">
                    timeoutMs
                  </label>
                  <input
                    id="pg-timeout"
                    type="number"
                    className="cf-input"
                    value={timeoutMs}
                    min={1000}
                    max={300_000}
                    onChange={(e) => setTimeoutMs(Number(e.target.value))}
                  />
                </div>
                {type === 'grid' && (
                  <>
                    <div>
                      <label className="cf-label" htmlFor="pg-rows">
                        rows / columns
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="pg-rows"
                          type="number"
                          className="cf-input"
                          value={rows}
                          min={1}
                          max={16}
                          onChange={(e) => setRows(Number(e.target.value))}
                        />
                        <input
                          type="number"
                          className="cf-input"
                          value={columns}
                          min={1}
                          max={16}
                          onChange={(e) => setColumns(Number(e.target.value))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="cf-label" htmlFor="pg-comment">
                        Hướng dẫn (comment)
                      </label>
                      <input
                        id="pg-comment"
                        className="cf-input"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="chọn tất cả ô có xe hơi"
                      />
                    </div>
                  </>
                )}
                {type === 'rotate' && (
                  <div>
                    <label className="cf-label" htmlFor="pg-angle">
                      Bước góc (độ)
                    </label>
                    <input
                      id="pg-angle"
                      type="number"
                      className="cf-input"
                      value={angle}
                      min={1}
                      max={180}
                      onChange={(e) => setAngle(Number(e.target.value))}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      2Captcha mặc định 40 (9 vị trí). Sai bước góc = đáp án gần như chắc chắn sai.
                    </p>
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => void run()}
              disabled={running}
              className="cf-btn cf-btn-primary mt-4 w-full"
            >
              {running ? 'Đang giải…' : 'Giải captcha'}
            </button>

            {error !== null && (
              <p className="mt-3 rounded-md bg-red-50 p-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
                {error}
              </p>
            )}
          </div>
        </div>

        {/* ================= cot phai: ket qua ================= */}
        <div className="space-y-4">
          <div className="cf-card p-4">
            <h2 className="mb-3 font-semibold">Kết quả</h2>

            {running && <Spinner />}

            {!running && result === null && (
              <p className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
                Chưa chạy lần nào. Bấm <strong>Giải captcha</strong> để bắt đầu.
                <br />
                <span className="text-xs">
                  Mẹo: hai loại <em>không cần model</em> chạy được ngay, không cần dữ liệu gì cả.
                </span>
              </p>
            )}

            {!running && result !== null && result.ok && (
              <div className="space-y-3">
                <div className="rounded-lg bg-emerald-50 p-4 dark:bg-emerald-950/40">
                  <p className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                    Đáp án
                  </p>
                  <p className="cf-mono mt-1 break-all text-2xl font-semibold text-emerald-900 dark:text-emerald-200">
                    {result.answer}
                  </p>
                </div>

                <div>
                  <div className="flex items-baseline justify-between text-xs text-slate-500">
                    <span>Độ tin cậy</span>
                    <span className="cf-mono">{(result.confidence ?? 0).toFixed(4)}</span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                    <div
                      className="h-full rounded-full bg-emerald-500"
                      style={{ width: `${Math.round((result.confidence ?? 0) * 100)}%` }}
                    />
                  </div>
                </div>

                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <dt className="text-slate-500">Solver</dt>
                  <dd className="cf-mono">{result.solver ?? '—'}</dd>
                  <dt className="text-slate-500">Nguồn</dt>
                  <dd className="cf-mono">{result.source ?? '—'}</dd>
                  <dt className="text-slate-500">Profile</dt>
                  <dd className="cf-mono">{result.profileKey ?? '—'}</dd>
                  <dt className="text-slate-500">Thời gian</dt>
                  <dd className="cf-mono">{result.latencyMs ?? '—'} ms</dd>
                </dl>

                {result.solution !== undefined && (
                  <div>
                    <p className="cf-label">solution (shape đầy đủ)</p>
                    <pre className="cf-mono overflow-x-auto rounded-md bg-slate-50 p-2 text-xs dark:bg-slate-950">
                      {JSON.stringify(result.solution, null, 2)}
                    </pre>
                  </div>
                )}

                <a href="#/tasks" className="cf-link text-sm">
                  Xem ban ghi o trang Task →
                </a>
              </div>
            )}

            {!running && result !== null && !result.ok && help !== null && (
              <div className="space-y-3">
                <div className="rounded-lg bg-amber-50 p-4 dark:bg-amber-950/40">
                  <div className="flex items-center gap-2">
                    <Badge tone="warn">{result.reason}</Badge>
                    {help.needsSetup && <Badge tone="info">thiếu bước chuẩn bị</Badge>}
                  </div>
                  <p className="mt-2 text-sm text-amber-900 dark:text-amber-200">{help.summary}</p>
                  {result.message !== undefined && (
                    <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">{result.message}</p>
                  )}
                </div>

                <div>
                  <p className="cf-label">Cần làm gì</p>
                  <ol className="mt-1 list-inside list-decimal space-y-1 text-sm text-slate-600 dark:text-slate-400">
                    {help.steps.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ol>
                </div>

                <p className="rounded-md bg-slate-50 p-2 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-400">
                  Không giải được <strong>không phải là lỗi</strong>: capforge trả về đúng mã lỗi có
                  thật của 2Captcha, nên project cũ của bạn xử lý được y hệt như khi gọi 2Captcha.
                </p>
              </div>
            )}
          </div>

          {/* --- doan ma --- */}
          {code !== null && (
            <div className="cf-card p-4">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <h2 className="font-semibold">Đoạn mã tương ứng</h2>
                <div className="ml-auto flex items-center gap-1">
                  {CODE_LANGS.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setLang(l.id)}
                      className={`rounded px-2 py-1 text-xs ${
                        l.id === lang
                          ? 'bg-sky-600 text-white'
                          : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-2 flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                  <input
                    type="checkbox"
                    checked={revealKey}
                    onChange={(e) => setRevealKey(e.target.checked)}
                  />
                  Hiện key thật
                </label>
                <span className="text-xs text-slate-400">
                  {revealKey ? 'Cẩn thận khi chia sẻ ảnh chụp màn hình.' : 'Key đang được thay bằng chỗ trống.'}
                </span>
                <div className="ml-auto">
                  <CopyButton value={code} />
                </div>
              </div>

              <pre className="cf-mono max-h-96 overflow-auto rounded-md bg-slate-950 p-3 text-xs leading-5 text-slate-100">
                {code}
              </pre>

              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Đoạn mã này được dựng từ chính request vừa chạy, nên nó không thể lệch với thực tế.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
